/* Multi-device sync, the pure part (no network, no DOM; tested in tests/app/sync.test.ts).
   - Every device keeps all entries it may see. It catches up by asking the database for entries changed since the newest
     change it has seen (updated_at), and for deletion notes since the newest one it has seen (entry_deletions).
   - Changes made on this device are queued as operations ("outbox"). They show on screen at once, are sent in order in
     the background, retried while there is no network, and re-applied on top of every catch-up until the server has them,
     so a slow phone never freezes and never loses a stopped timer. */
import type {Entry, Timer} from './model.ts';

/** names: the project's names when the change was made (kept with a refused change: after an unassignment the person may no
    longer see that project) */
export type Op = ({opId: string; kind: 'saveEntry'; entry: Entry; isNew: boolean}
  | {opId: string; kind: 'deleteEntry'; entryId: string; memberId: string}
  | {opId: string; kind: 'startTimer'; timer: Timer}
  | {opId: string; kind: 'stopTimer'; timer: Timer; entry: Entry}
  | {opId: string; kind: 'updateTimer'; prev: Timer; timer: Timer})   // a running timer's details or start time changed (same person)
  & {names?: {project: string; client: string};
     /** when the change was really made on the device (the tap; migration 016 merges every device's changes in this order).
         Changes queued by an older app version have none: see lib/merge.ts opMadeAt. */
     madeAt?: string;
     /** a Not saved item tried again once by itself after the 2026-10-30 update (the new merge rules) */
     autoRetried?: true};

/** what an operation looks like on this device before the server has confirmed it (applied again after every catch-up) */
export function applyOp(entries: Map<string, Entry>, timers: Timer[], op: Op): Timer[] {
  switch (op.kind) {
    case 'saveEntry': entries.set(op.entry.id, {...entries.get(op.entry.id), ...op.entry, updatedAt: entries.get(op.entry.id)?.updatedAt}); return timers;
    case 'deleteEntry': entries.delete(op.entryId); return timers;
    case 'startTimer': return [...timers.filter(t => t.memberId !== op.timer.memberId), op.timer];
    case 'stopTimer':
      if (!entries.has(op.entry.id)) entries.set(op.entry.id, op.entry);   // the server's copy (if any) wins: whoever stopped first
      return timers.filter(t => !(t.memberId === op.timer.memberId && sameInstant(t.startedAt, op.timer.startedAt)));
    case 'updateTimer':   // only the timer it was made for: if that one was stopped meanwhile, nothing changes
      return timers.map(t => t.memberId === op.prev.memberId && sameInstant(t.startedAt, op.prev.startedAt) ? {...op.timer, memberId: t.memberId} : t);
  }
}
export const sameInstant = (a: string, b: string) => Date.parse(a) === Date.parse(b);

export interface Deletion { id: string; deleted_at: string }
/** Merge a catch-up into the local entries: deletions first, then changed rows (a re-added entry is newer than its deletion). */
export function mergeChanges(entries: Map<string, Entry>, changed: Entry[], deletions: Deletion[]): {removed: number; upserted: number} {
  let removed = 0, upserted = 0;
  for (const d of deletions) {
    const e = entries.get(d.id);
    if (e && !(e.updatedAt && Date.parse(e.updatedAt) > Date.parse(d.deleted_at))) { entries.delete(d.id); removed++; }
  }
  for (const e of changed) {
    const old = entries.get(e.id);
    if (!old || !old.updatedAt || !e.updatedAt || Date.parse(e.updatedAt) >= Date.parse(old.updatedAt)) { entries.set(e.id, e); upserted++; }
  }
  return {removed, upserted};
}
/** newest timestamp in a list (null if none) */
export function newest(stamps: (string | undefined | null)[], current: string | null): string | null {
  let best = current, bestMs = current ? Date.parse(current) : -Infinity;
  for (const s of stamps) if (s) { const ms = Date.parse(s); if (ms > bestMs) { best = s; bestMs = ms; } }
  return best;
}
/** A save that started earlier can become visible a little later than one that started after it. So a catch-up also asks
    again for the last few minutes before the newest change seen — but only for changes that may not have been visible at
    the previous catch-up (`prevStart`, this device's clock when it last asked). Anything older than that was settled
    already, so after a big change (a merge, an import, a migration) devices do not fetch those rows again and again. */
export const OVERLAP_MS = 5 * 60 * 1000;
export function since(stamp: string | null, prevStart?: number | null): string {
  if (!stamp) return '1970-01-01T00:00:00Z';
  const w = Date.parse(stamp), settled = prevStart ? prevStart - OVERLAP_MS : -Infinity;
  return new Date(Math.min(w, Math.max(w - OVERLAP_MS, settled))).toISOString();
}

/** a request that failed because of the network (or the server was briefly unavailable) is retried; anything else is final */
export function isRetryable(err: {status?: number; code?: string; message?: string} | null | undefined): boolean {
  if (!err) return false;
  const s = err.status ?? 0;
  if (s === 0 || s === 401 || s === 408 || s === 425 || s === 429 || s >= 500) return true;
  return /fetch|network|timeout|abort|load failed|connection/i.test(err.message || '');
}
